import { useEffect, useState } from 'react';
import { Badge, Col, Row } from 'react-bootstrap';
import GameTypeBadge from '../../GameTypeBadge';
import { Collection, PeopleFill, PersonFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { getAccessToken } from '../../../functions/authentication';
import { fetchFriendGames, fetchFriendList } from '../../../functions/friends';
import Loader from '../../Loader';
import PageLayout from '../../PageLayout';

function Friends({ onError }) {
    const { t } = useTranslation('messages');
    const [friends, setFriends] = useState([]);
    const [loadingFriends, setLoadingFriends] = useState(true);
    const [selectedFriend, setSelectedFriend] = useState(null);
    const [games, setGames] = useState([]);
    const [loadingGames, setLoadingGames] = useState(false);

    useEffect(() => {
        fetchFriendList(
            getAccessToken(),
            (data) => {
                setFriends(data.friends);
                setLoadingFriends(false);
            },
            (errorCode, errorDescription) => {
                setLoadingFriends(false);
                onError(errorCode, errorDescription);
            }
        );
    }, [onError]);

    function onSelectFriend(friend) {
        setSelectedFriend(friend);
        setGames([]);
        setLoadingGames(true);
        fetchFriendGames(
            getAccessToken(),
            (data) => {
                setGames(data.games);
                setLoadingGames(false);
            },
            (errorCode, errorDescription) => {
                setLoadingGames(false);
                onError(errorCode, errorDescription);
            },
            friend.userId
        );
    }

    return (
        <PageLayout
            heroPadding="py-4"
            hero={<h2 className="fw-bold mb-0">{t('friends.title')}</h2>}
        >
            <Row className="g-4">
                <Col xs={12} lg={3}>
                    <h6 className="fw-semibold text-muted text-uppercase small mb-3">
                        {t('friends.list_title')}
                    </h6>
                    <FriendList
                        friends={friends}
                        loading={loadingFriends}
                        selectedFriend={selectedFriend}
                        onSelect={onSelectFriend}
                        t={t}
                    />
                </Col>
                <Col xs={12} lg={9}>
                    <h6 className="fw-semibold text-muted text-uppercase small mb-3">
                        {selectedFriend
                            ? t('friends.games_title', { name: selectedFriend.firstname })
                            : t('friends.games_title_empty')}
                    </h6>
                    <FriendGames
                        loading={loadingGames}
                        games={games}
                        selectedFriend={selectedFriend}
                        t={t}
                    />
                </Col>
            </Row>
        </PageLayout>
    );
}

function FriendList({ friends, loading, selectedFriend, onSelect, t }) {
    if (loading) return <Loader />;

    if (friends.length === 0) {
        return (
            <div className="text-center py-4 text-muted">
                <PeopleFill size={36} className="mb-2 opacity-25" />
                <p className="small mb-0">{t('friends.empty_state')}</p>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column gap-2">
            {friends.map((friend) => {
                const isSelected = selectedFriend?.friendId === friend.friendId;
                return (
                    <button
                        key={friend.friendId}
                        onClick={() => onSelect(friend)}
                        className={`btn text-start p-3 border rounded-3 ${isSelected ? 'btn-primary' : 'btn-light border-0 shadow-sm'}`}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                    width: 40, height: 40,
                                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#e9d5ff',
                                    color: isSelected ? '#fff' : '#6d28d9'
                                }}
                            >
                                <PersonFill size={20} />
                            </div>
                            <div className="min-w-0">
                                <div className={`fw-semibold small ${isSelected ? 'text-white' : 'text-dark'}`}>
                                    {friend.firstname} {friend.lastname}
                                </div>
                                <div className={`small text-truncate ${isSelected ? 'text-white-50' : 'text-muted'}`}>
                                    @{friend.username}
                                </div>
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

function FriendGames({ loading, games, selectedFriend, t }) {
    if (!selectedFriend) {
        return (
            <div className="text-center py-5 text-muted">
                <PeopleFill size={48} className="mb-3 opacity-25" />
                <p className="mb-0">{t('friends.select_friend_hint')}</p>
            </div>
        );
    }

    if (loading) return <Loader />;

    if (games.length === 0) {
        return (
            <div className="text-center py-5 text-muted">
                <Collection size={48} className="mb-3 opacity-25" />
                <p className="mb-0">{t('friends.games_empty_state')}</p>
            </div>
        );
    }

    return (
        <div>
            {games.map((game) => (
                <div key={game.favouriteGameId} className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3 d-flex gap-3 align-items-start">
                        {game.imageLink && (
                            <img
                                src={game.imageLink}
                                alt={game.name}
                                className="rounded flex-shrink-0"
                                style={{ width: 72, height: 72, objectFit: 'cover' }}
                            />
                        )}
                        <div className="flex-grow-1 min-w-0">
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <a
                                    href={game.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fw-semibold text-dark text-decoration-none"
                                >
                                    {game.name}
                                </a>
                                <GameTypeBadge gameType={game.gameType} />
                            </div>
                            {game.description && (
                                <p className="text-clamp text-muted small mb-2 mt-1">
                                    {game.description}
                                </p>
                            )}
                            {game.gameLabels && game.gameLabels.length > 0 && (
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                    {game.gameLabels.map((label) => (
                                        <Badge key={label.gameLabelId} bg="secondary">
                                            {label.name}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                            <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                                {game.lastPlayedAt ? (
                                    <span className="text-muted small">
                                        {t('friends.last_played_at', { date: new Date(game.lastPlayedAt).toLocaleDateString() })}
                                    </span>
                                ) : (
                                    <span className="text-muted small">{t('friends.last_played_at_never')}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default Friends;
